import moment from "moment";
import { IUserItem, IHighlighterItem, IArchitectItem, IStageItem, ITaskItem, IUserDataState } from "../../@types";

const truncateDescription = (description: string, wordLimit: number = 10): string => {
    if (!description) return "";
    const words = description.trim().split(/\s+/);
    if (words.length > wordLimit) {
        return words.slice(0, wordLimit).join(" ") + "...";
    }
    return description;
};

const getFirstSubtaskTitle = (description: string): string => {
    if (!description) return "";
    const clean = description.replace(/\*\*/g, '').trim();
    const firstItem = clean.split(/,|\n|-|:|\./)[0].trim();
    return truncateDescription(firstItem, 4);
};

export const getUserName = (
    userId?: string | null,
    userData?: IUserDataState | IUserItem[] | null,
    ...restUserLists: (IUserItem[] | undefined)[]
): string => {
    if (!userId) return '-';

    const lists: (IUserItem[] | undefined)[] = [];
    if (Array.isArray(userData)) {
        lists.push(userData);
    } else if (userData && typeof userData === 'object') {
        const uData = userData as IUserDataState;
        lists.push(
            uData.allProjectManagers,
            uData.allTaskManagers,
            uData.allProjectWorkers,
            uData.allTaskWorkers
        );
    }
    lists.push(...restUserLists);

    for (const list of lists) {
        if (Array.isArray(list)) {
            const user = list.find((u: IUserItem) => u?._id === userId);
            if (user) {
                const fullName = `${user.FirstName || ''} ${user.LastName || ''}`.trim();
                if (fullName) return fullName;
            }
        }
    }

    if (typeof userId === 'string' && /^[0-9a-fA-F]{24}$/.test(userId)) {
        return '-';
    }

    return userId;
};

export const AllActivities = (
    activity: string,
    changes?: Record<string, unknown> | null,
    oldData?: Record<string, unknown> | null,
    allProjectManagers?: IUserItem[],
    allTaskManagers?: IUserItem[],
    allProjectWorkers?: IUserItem[],
    allTaskWorkers?: IUserItem[],
    allHighlighters?: IHighlighterItem[],
    allArchitects?: IArchitectItem[],
    isModal: boolean = false,
    allStages: IStageItem[] = [],
    allTasks: ITaskItem[] = [],
    allTaskSow: unknown[] | Record<string, unknown> | unknown = []
): string => {
    const formatTime = (ms: number) => {
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        if (hours === 0 && minutes === 0 && ms > 0) {
            const seconds = Math.round(ms / 1000);
            return `${seconds}s`;
        }
        return `${hours}h ${minutes}m`;
    };
    const formatHoursToHrMin = (val: string | number) => {
        const num = typeof val === 'number' ? val : parseFloat(val);
        if (isNaN(num)) return String(val);
        const totalMinutes = Math.round(num * 60);
        const hrs = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        return `${hrs}h ${mins.toString().padStart(2, '0')}min`;
    };
    const isDurationKey = (k: string) => {
        const lk = k.toLowerCase();
        return lk.includes('timetaken') || lk.includes('timespend') || lk.includes('timespent') || lk.includes('timeremaining') || lk.includes('spenttime') || lk.includes('elapsedtime');
    };
    const isDateTimeKey = (k: string) => {
        const lk = k.toLowerCase();
        return lk.includes('endtime') || lk.includes('starttime') || lk === 'duedate' || lk === 'stageduedate' || lk === 'clientduedate' || lk === 'meetingdate';
    };

    const getStageNameFromMaster = (): string => {
        const updateObj = (changes?.updateObject || oldData?.updateObject || changes?.payload || oldData?.payload) as Record<string, unknown> | undefined;
        const stageVal = (changes?.stageId || oldData?.stageId || changes?.stageDataId || oldData?.stageDataId || updateObj?.stageId || updateObj?._id || changes?._id || changes?.id) as Record<string, unknown> | string | undefined;
        let name = "";
        if (stageVal && typeof stageVal === 'object') {
            const tempName = (stageVal?.temporaryName as string | undefined)?.trim();
            const origName = (stageVal?.name as string | undefined)?.trim();
            if (tempName) {
                name = tempName;
            } else if (origName) {
                name = origName;
            }
        }
        if (!name) {
            const stageId = typeof stageVal === 'string' ? stageVal : (stageVal?._id || stageVal?.id) as string | undefined;
            if (stageId && Array.isArray(allStages)) {
                const found = allStages.find((s: IStageItem) => s?._id === stageId || s?.id === stageId);
                if (found?.name) name = found.name;
            }
            if (!name) {
                const fallbackTemp = (changes?.temporaryName || oldData?.temporaryName || updateObj?.temporaryName) as string | undefined;
                const fallbackName = (changes?.name || changes?.stageName || oldData?.name || oldData?.stageName || updateObj?.name || updateObj?.stageName) as string | undefined;
                name = (fallbackTemp?.trim() || fallbackName?.trim() || "") as string;
            }
        }
        return name ? name.trim() : "";
    };

    const getTaskNameFromMaster = (currentKey?: string): string => {
        const updateObj = (changes?.updateObject || oldData?.updateObject || changes?.payload || oldData?.payload) as Record<string, unknown> | undefined;

        const getItemTaskName = (item: unknown): string => {
            if (!item) return '';
            if (typeof item === 'string') {
                if (Array.isArray(allTasks)) {
                    const found = allTasks.find((t: ITaskItem) => t?._id === item || t?.id === item);
                    if (found?.name) return found.name;
                    if (found?.shortForm) return found.shortForm;
                }
                return '';
            }
            if (typeof item === 'object' && item !== null) {
                const itemObj = item as Record<string, unknown>;
                if (itemObj.name) return String(itemObj.name);
                if (itemObj.shortForm) return String(itemObj.shortForm);
                if (itemObj.taskName) return String(itemObj.taskName);
                const tIdObj = itemObj.taskId;
                const tId = typeof tIdObj === 'object' && tIdObj !== null ? ((tIdObj as Record<string, unknown>)._id || (tIdObj as Record<string, unknown>).id) : (tIdObj || itemObj._id || itemObj.id);
                if (tId && typeof tId === 'string' && Array.isArray(allTasks)) {
                    const found = allTasks.find((t: ITaskItem) => t?._id === tId || t?.id === tId);
                    if (found?.name) return found.name;
                    if (found?.shortForm) return found.shortForm;
                }
            }
            return '';
        };

        if (currentKey && (currentKey.startsWith('tasksData.') || currentKey.startsWith('tasks.'))) {
            const parts = currentKey.split('.');
            const idx = parseInt(parts[1], 10);
            if (!isNaN(idx)) {
                const tasksDataObj = (changes?.tasksData || oldData?.tasksData || updateObj?.tasksData || changes?.tasks || oldData?.tasks) as unknown[];
                const item = tasksDataObj?.[idx];
                const res = getItemTaskName(item);
                if (res) return res;
            }
        }

        const tasksDataArr = (changes?.tasksData || oldData?.tasksData || updateObj?.tasksData || changes?.tasks || oldData?.tasks) as unknown[];
        if (Array.isArray(tasksDataArr) && tasksDataArr.length > 0) {
            for (const item of tasksDataArr) {
                const res = getItemTaskName(item);
                if (res) return res;
            }
        }

        const tasksObj = (changes?.taskId || oldData?.taskId || changes?.Tasks || changes?.tasks || oldData?.Tasks || oldData?.tasks || updateObj?.taskId || updateObj?.Tasks || updateObj?.tasks) as Record<string, unknown> | string | undefined;
        const resObj = getItemTaskName(tasksObj);
        if (resObj) return resObj;

        const taskId = typeof tasksObj === 'string' ? tasksObj : (tasksObj?._id || tasksObj?.id || tasksObj?.taskId || changes?._id || oldData?._id);
        if (taskId && typeof taskId === 'string' && Array.isArray(allTasks)) {
            const found = allTasks.find((t: ITaskItem) => t?._id === taskId || t?.id === taskId);
            if (found?.name) return found.name;
            if (found?.shortForm) return found.shortForm;
        }

        return (changes?.name || changes?.taskName || oldData?.name || oldData?.taskName || updateObj?.name || updateObj?.taskName || "") as string;
    };

    const mapValue = (key: string, value: unknown): string => {
        if (key === 'manager' || key === 'projectManager') {
            const manager = allProjectManagers?.find((pm: IUserItem) => pm._id === value) || allTaskManagers?.find((tm: IUserItem) => tm._id === value);
            return manager ? `${manager.FirstName || ''} ${manager.LastName || ''}`.trim() : "-";
        } else if (key === 'worker' || key === 'projectWorker') {
            const worker = allProjectWorkers?.find((w: IUserItem) => w._id === value) || allTaskWorkers?.find((tm: IUserItem) => tm._id === value);
            return worker ? `${worker.FirstName || ''} ${worker.LastName || ''}`.trim() : "-";
        } else if (key === 'salesRepresentative' || key.toLowerCase().includes('salesrepresentative')) {
            const resolvedName = getUserName(String(value), allProjectManagers, allTaskManagers, allProjectWorkers, allTaskWorkers);
            if (resolvedName && resolvedName !== '-') return resolvedName;
            const user = allProjectWorkers?.find((w: IUserItem) => w._id === value) || allTaskWorkers?.find((tm: IUserItem) => tm._id === value) || allProjectManagers?.find((pm: IUserItem) => pm._id === value);
            return user ? `${user.FirstName || ''} ${user.LastName || ''}`.trim() : (typeof value === 'string' ? value : "-");
        } else if (key === 'architect') {
            const architect = allArchitects?.find((arc: IArchitectItem) => arc?._id === value);
            return architect ? (architect?.name || "-") : "-";
        } else if (key === 'attachments') {
            return key || '-';
        }
        else if (key === 'highlighters') {
            const highlighterNames = Array.isArray(value) ? value?.map((id: string) => {
                const highlighter = allHighlighters?.find((hl: IHighlighterItem) => hl?._id === id);
                return highlighter ? highlighter.name : id;
            }).join(", ") : String(value ?? "");
            return highlighterNames || "-";
        } else if (key.toLowerCase().includes('sowmapping')) {
            const sowNames = Array.isArray(value) ? value?.map((item: unknown) => {
                if (typeof item === 'object' && item !== null) {
                    const obj = item as Record<string, unknown>;
                    return String(obj?.name || obj?.title || obj?._id || obj?.id || '');
                }
                return String(item);
            }).filter(Boolean).join(", ") : String(value ?? "");
            return sowNames || "-";
        } else if ((key.startsWith('tasksNameList') || key.startsWith('tasksData')) && Array.isArray(value)) {
            const shortForms = (value as Array<{ shortForm?: string; name?: string }>)?.map((task) => task?.name || task?.shortForm).join(' , ');
            return shortForms;
        } else if (isDurationKey(key)) {
            const num = typeof value === 'number' ? value : Number(value);
            return !isNaN(num) ? formatTime(num) : String(value || "0h 0m");
        } else if (isDateTimeKey(key)) {
            const num = typeof value === 'number' ? value : (!isNaN(Number(value)) && String(value).length > 5 ? Number(value) : null);
            const isTimeField = key.toLowerCase().includes('endtime') || key.toLowerCase().includes('starttime');
            const dateFormat = isTimeField ? 'MMM DD, YYYY LT' : 'MMM DD, YYYY';
            if (num !== null) {
                return moment(num).format(dateFormat);
            }
            return value ? moment(value as string).format(dateFormat) : "-";
        } else if (key === 'timeEstimate') {
            return formatHoursToHrMin(value as string | number);
        } else if (typeof value === 'boolean') {
            return value ? "Yes" : "No";
        } else if (value === 'true' || value === 'false') {
            return value === 'true' ? "Yes" : "No";
        } else if (key.startsWith('stagesNameList') || (key.startsWith('stagesData') && Array.isArray(value))) {
            const stageNames = (value as Array<{ name?: string; temporaryName?: string }>)?.map((stage) => stage?.temporaryName || stage?.name)?.filter(Boolean)?.join(", ");
            return stageNames || "-";
        }
        else if (key.startsWith('contracts')) {
            if (typeof value === 'object' && value !== null) {
                const recordVal = value as Record<string, unknown>;
                const propName = recordVal?.proposalName || recordVal?.name;
                if (propName) return String(propName);
            }
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
                const m = moment(value);
                if (m.isValid()) return m.format('MMM DD, YYYY');
            }
            if (typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value.trim())) {
                const userName = getUserName(value.trim(), allProjectManagers, allTaskManagers, allProjectWorkers, allTaskWorkers);
                if (userName && userName !== '-') return userName;
            }
            return value === null || value === "" || value === undefined || value === "undefined" ? "-" : String(value);
        }
        else if (typeof value === 'object' && value !== null) {
            const recordVal = value as Record<string, unknown>;
            return Object.keys(recordVal)?.map((nestedKey) => {
                if (isDurationKey(nestedKey)) {
                    const num = typeof recordVal[nestedKey] === 'number' ? (recordVal[nestedKey] as number) : Number(recordVal[nestedKey]);
                    return `${nestedKey}: ${!isNaN(num) ? formatTime(num) : String(recordVal[nestedKey] || "0h 0m")}`;
                } else if (isDateTimeKey(nestedKey)) {
                    const val = recordVal[nestedKey];
                    const num = typeof val === 'number' ? val : (!isNaN(Number(val)) && String(val).length > 5 ? Number(val) : null);
                    const isTimeField = nestedKey.toLowerCase().includes('endtime') || nestedKey.toLowerCase().includes('starttime');
                    const dateFormat = isTimeField ? 'MMM DD, YYYY LT' : 'MMM DD, YYYY';
                    const formatted = num !== null ? moment(num).format(dateFormat) : (val ? moment(val as string).format(dateFormat) : "-");
                    return `${nestedKey}: ${formatted}`;
                } else if (nestedKey.startsWith('stagesNameList') || nestedKey.startsWith('stagesData')) {
                    const stageNames = (recordVal[nestedKey] as Array<{ name?: string; temporaryName?: string }>)?.map((stage) => stage?.temporaryName || stage?.name)?.filter(Boolean)?.join(", ");
                    return `Stages: ${stageNames}`;
                } else if (nestedKey.startsWith('tasksNameList') || nestedKey.startsWith('tasksData')) {
                    const taskNames = (recordVal[nestedKey] as Array<{ shortForm?: string; name?: string }>)?.map((task) => task?.name || task?.shortForm)?.join(", ");
                    return `Tasks: ${taskNames}`;
                } else if (nestedKey === 'description' || nestedKey === 'projectSow.description') {
                    return `Description: ${isModal ? String(recordVal[nestedKey] ?? '') : truncateDescription(String(recordVal[nestedKey] ?? ''))}`;
                }
                else {
                    return `${nestedKey}: ${recordVal[nestedKey] === null || recordVal[nestedKey] === "" || recordVal[nestedKey] === undefined ? "-" : String(recordVal[nestedKey])}`;
                }
            }).join(', ');
        } else {
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
                const m = moment(value);
                if (m.isValid()) return m.format('MMM DD, YYYY');
            }
            return value === null || value === "" || value === undefined || value === "undefined" ? "-" : String(value);
        }
    };

    const handleActionUpdate = (changes?: Record<string, unknown> | null, oldData?: Record<string, unknown> | null): string => {
        if (!changes) return "";

        const updateObj = changes?.updateObject as Record<string, unknown> | undefined;
        const isSubtaskDeletedKey =
            Object.keys(changes).some(k => k.toLowerCase().includes('subtaskdeleted')) ||
            changes?.subtaskDeleted === true || changes?.subtaskDeleted === 'true' ||
            updateObj?.subtaskDeleted === true || updateObj?.subtaskDeleted === 'true';

        if (isSubtaskDeletedKey) {
            return "subtask is deleted";
        }

        const descriptionKeys = Object.keys(changes).filter(key => key.match(/^payload\.Description\.\d+$/));
        if (descriptionKeys.length > 0 && descriptionKeys.every(key => typeof changes[key] === 'object')) {
            const names = descriptionKeys.map(k => {
                const item = changes[k] as Record<string, unknown>;
                return String(item?.name || item?.description || item?.comment || item?.title || "");
            }).filter(Boolean);
            if (names.length === 1 && !names[0].includes(',') && !names[0].includes('\n')) {
                const first = truncateDescription(names[0], 10);
                if (first) {
                    return `created subtask: "${first}"`;
                }
            }
            return "created subtasks";
        }

        const descMatchKeys = Object.keys(changes).filter(key => key.match(/^payload\.Description\.\d+\./));
        if (descMatchKeys.length > 0) {
            const priorityKey = descMatchKeys.find(key => {
                const m = key.match(/^payload\.Description\.\d+\.(name|description|comment|title|isReviewed|isDone|isChecked)$/);
                return m;
            }) || descMatchKeys.find(key => !key.includes('updatedBy'));

            if (priorityKey) {
                const match = priorityKey.match(/^payload\.Description\.(\d+)\.(.+)$/);
                if (match) {
                    const idx = parseInt(match[1], 10);
                    const fieldName = match[2];
                    const val = changes[priorityKey];

                    const oldPayload = oldData?.payload as { Description?: Array<Record<string, unknown>> } | undefined;
                    const changesPayload = changes?.payload as { Description?: Array<Record<string, unknown>> } | undefined;
                    const firstSubtaskItem = oldPayload?.Description?.[0] || changesPayload?.Description?.[0] || oldPayload?.Description?.[idx] || changesPayload?.Description?.[idx];
                    const rawSubtaskName = String(firstSubtaskItem?.name || firstSubtaskItem?.description || firstSubtaskItem?.comment || firstSubtaskItem?.title || "");
                    const displaySubtaskName = getFirstSubtaskTitle(rawSubtaskName);
                    const nameLabel = displaySubtaskName ? `: "${displaySubtaskName}"` : '';

                    if (fieldName === 'name' || fieldName === 'description' || fieldName === 'comment' || fieldName === 'title') {
                        const oldVal = String(oldPayload?.Description?.[idx]?.[fieldName] || oldPayload?.Description?.[idx]?.name || oldPayload?.Description?.[idx]?.description || oldData?.[priorityKey] || "");
                        const newName = String(val || rawSubtaskName);
                        const displayOldName = getFirstSubtaskTitle(oldVal);
                        const displayNewName = getFirstSubtaskTitle(newName);

                        if (oldVal && oldVal.trim() && displayOldName && displayNewName && displayOldName.toLowerCase() !== displayNewName.toLowerCase()) {
                            return `updated subtask from "${displayOldName}" to "${displayNewName}"`;
                        }
                        if (newName.includes(',') || newName.includes('\n')) {
                            return "created subtasks";
                        }
                        if (displayNewName) {
                            return `created subtask: "${displayNewName}"`;
                        }
                        return "created subtasks";
                    }
                    if (idx > 0 && (fieldName === 'isReviewed' || fieldName === 'isDone' || fieldName === 'isChecked')) {
                        return "";
                    }
                    if (fieldName === 'isReviewed') {
                        return val === true || val === 'true'
                            ? `reviewed subtask${nameLabel}`
                            : `unreviewed subtask${nameLabel}`;
                    }
                    if (fieldName === 'isDone' || fieldName === 'isChecked') {
                        return val === true || val === 'true'
                            ? `completed subtask${nameLabel}`
                            : `uncompleted subtask${nameLabel}`;
                    }
                    return `updated ${fieldName} to ${String(val ?? '')}${displaySubtaskName ? ` for subtask "${displaySubtaskName}"` : ''}`;
                }
            }
        }

        const updateObject = changes?.updateObject as Record<string, unknown> | undefined;
        const oldUpdateObj = oldData?.updateObject as Record<string, unknown> | undefined;
        if (updateObject) {
            const rawSubtaskName = String(updateObject?.name || updateObject?.description || updateObject?.title || "");
            const oldRawSubtaskName = String(oldUpdateObj?.name || oldUpdateObj?.description || oldUpdateObj?.title || "");
            const displaySubtaskName = getFirstSubtaskTitle(rawSubtaskName);
            const displayOldSubtaskName = getFirstSubtaskTitle(oldRawSubtaskName);
            const nameLabel = displaySubtaskName ? `: "${displaySubtaskName}"` : '';

            const objIdx = Number(updateObject?.index ?? updateObject?.idx ?? 0);
            if (objIdx > 0 && (updateObject.isReviewed !== undefined || updateObject.isDone !== undefined || updateObject.isChecked !== undefined)) {
                return "";
            }

            if (updateObject.isReviewed !== undefined) {
                return updateObject.isReviewed === true || updateObject.isReviewed === 'true'
                    ? `reviewed subtask${nameLabel}`
                    : `unreviewed subtask${nameLabel}`;
            }
            if (updateObject.isDone !== undefined || updateObject.isChecked !== undefined) {
                const isCompleted = updateObject.isDone === true || updateObject.isDone === 'true' || updateObject.isChecked === true || updateObject.isChecked === 'true';
                return isCompleted ? `completed subtask${nameLabel}` : `uncompleted subtask${nameLabel}`;
            }
            if (updateObject.name && oldRawSubtaskName && updateObject.name !== oldRawSubtaskName) {
                return `updated subtask from "${displayOldSubtaskName}" to "${displaySubtaskName}"`;
            }
            return `updated subtask details${displaySubtaskName ? ` for "${displaySubtaskName}"` : ''}`;
        }

        return "";
    };

    const filteredChanges: Array<{ isNew: boolean; text: string }> = [];
    const roleChanges: string[] = [];
    const actionChanges: string[] = [];

    if (changes) {
        Object?.keys(changes)?.forEach((key) => {
            if (key !== 'updatedAt' && key !== 'updatedBy' && key !== 'lastStatus' && key.toLowerCase() !== 'clientemailid' && key !== 'taskId' && key !== 'stageId' && key !== 'stageDataId' && key !== 'projectId' && key.toLowerCase() !== '_id' && key.toLowerCase() !== 'id' && key.toLowerCase() !== '__v' && key.toLowerCase() !== 'actionname') {
                if (key.toLowerCase().includes('subtaskdeleted') || key.toLowerCase().includes('isdeleted')) return;
                if (key.startsWith('tasksNameList') && Object.keys(changes).some(k => k.startsWith('tasksData'))) return;
                if (key.startsWith('stagesNameList') && Object.keys(changes).some(k => k.startsWith('stagesData'))) return;
                if (key.toLowerCase() === 'createdat') return;
                if (key.toLowerCase().includes('attachment')) {
                    const actionName = String(changes?.actionName || changes?.action || '').toLowerCase();
                    const isDel = changes[key] === null || changes[key] === undefined || changes?.isDeleted === true || changes?.isDeleted === 'true' || actionName.includes('del') || actionName.includes('remove');

                    const getAttachmentNames = (val: unknown): string[] => {
                        if (!val) return [];
                        const extractName = (item: unknown): string => {
                            if (!item) return '';
                            if (typeof item === 'string') {
                                const clean = item.split('?')[0];
                                return clean.split('/').pop() || item;
                            }
                            if (typeof item === 'object' && item !== null) {
                                const itemObj = item as Record<string, unknown>;
                                const raw = (itemObj.originalName || itemObj.name || itemObj.fileName || itemObj.title || itemObj.file || itemObj.url || '') as string;
                                if (typeof raw === 'string') {
                                    const clean = raw.split('?')[0];
                                    return clean.split('/').pop() || raw;
                                }
                            }
                            return '';
                        };

                        if (Array.isArray(val)) {
                            return val.map(extractName).filter(Boolean);
                        }
                        const single = extractName(val);
                        return single ? [single] : [];
                    };

                    let names = getAttachmentNames(changes[key]);
                    if (!names.length && isDel && oldData) {
                        names = getAttachmentNames(oldData[key]);
                    }

                    let text = isDel ? 'deleted attachments' : 'uploaded attachments';
                    if (names.length > 0) {
                        const formattedNames = names.map(n => `"${n}"`).join(', ');
                        text = isDel
                            ? (names.length === 1 ? `deleted attachment ${formattedNames}` : `deleted attachments: ${formattedNames}`)
                            : (names.length === 1 ? `uploaded attachment ${formattedNames}` : `uploaded attachments: ${formattedNames}`);
                    }

                    if (!actionChanges.includes(text)) {
                        actionChanges.push(text);
                    }
                    return;
                }
                const valStr = String(changes[key] || '').toLowerCase().replace(/[\s_-]/g, '');
                const isStatusKey = key.toLowerCase().includes('status') || key.toLowerCase() === 'tasks' || key.toLowerCase() === 'abtdstatus';

                if (key === 'startTime' || key === 'category' || (isStatusKey && (valStr === 'inprogress' || valStr === 'ip'))) {
                    if (!actionChanges.includes('started timer')) {
                        actionChanges.push('started timer');
                    }
                    return;
                }

                let displayKey = key.charAt(0).toUpperCase() + key.slice(1);
                if (key.toLowerCase() === 'description' || key.toLowerCase() === 'name' || key.toLowerCase() === 'meetingnotes') displayKey = 'Description';
                if (key === 'tmQuote') displayKey = 'T&M Quote';
                if (key.toLowerCase().includes('specdocument') || key === 'specDocuments') displayKey = 'Spec Documents';
                if (key === 'cityName') displayKey = 'City';
                if (key === 'stateName') displayKey = 'State';
                if (key.startsWith('tasksNameList') || key.startsWith('tasksData')) displayKey = 'Tasks';
                if (key.startsWith('stagesNameList') || key.startsWith('stagesData')) displayKey = 'Stages';
                if (key.startsWith('contracts')) displayKey = 'Contracts';
                if (key.toLowerCase().includes('sowmapping')) displayKey = 'SowMapping';

                const getNestedValue = (obj: Record<string, unknown> | null | undefined, path: string): unknown => {
                    if (!obj || !path) return undefined;
                    if (path in obj) return obj[path];
                    return path.split('.').reduce<unknown>((acc, part) => {
                        if (acc && typeof acc === 'object' && acc !== null && part in (acc as Record<string, unknown>)) {
                            return (acc as Record<string, unknown>)[part];
                        }
                        return undefined;
                    }, obj);
                };

                const rawOldValue = getNestedValue(oldData, key);
                let oldValue = rawOldValue !== undefined ? mapValue(key, rawOldValue) : "-";
                const newValue = mapValue(key, changes[key]);

                if (oldValue !== '-' && newValue !== '-' && oldValue.trim().toLowerCase() === newValue.trim().toLowerCase()) return;

                if (key === 'projectManager') {
                    if (oldValue === '-' || !oldData?.[key]) {
                        roleChanges.push(`assigned ${newValue} as project manager`);
                    } else {
                        filteredChanges.push({ isNew: false, text: `ProjectManager from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'architect') {
                    if (oldValue === '-' || !oldData?.[key]) {
                        roleChanges.push(`assigned ${newValue} as an architect`);
                    } else {
                        filteredChanges.push({ isNew: false, text: `Architect from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'salesRepresentative') {
                    if (oldValue === '-' || !oldData?.[key]) {
                        roleChanges.push(`assigned ${newValue} as sales representative`);
                    } else {
                        filteredChanges.push({ isNew: false, text: `SalesRepresentative from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'manager') {
                    if (oldValue === '-' || !oldData?.[key]) {
                        roleChanges.push(`assigned ${newValue} as manager`);
                    } else {
                        filteredChanges.push({ isNew: false, text: `Manager from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'worker' || key === 'projectWorker') {
                    if (oldValue === '-' || !oldData?.[key]) {
                        roleChanges.push(`assigned ${newValue} as worker`);
                    } else {
                        filteredChanges.push({ isNew: false, text: `Worker from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'clientAddress') {
                    if (oldValue === '-' || !oldData?.[key]) {
                        filteredChanges.push({ isNew: true, text: `client address ${newValue}` });
                    } else {
                        filteredChanges.push({ isNew: false, text: `client address from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'quoteNumber' && oldValue === '-') {
                    filteredChanges.push({ isNew: true, text: `QuoteNumber to ${newValue}` });
                } else if (key.startsWith('tasksNameList') || key.startsWith('tasksData') || key.toLowerCase() === 'tasks') {
                    const oldArr = oldData?.[key];
                    const newArr = changes?.[key];
                    const isOldEmpty = !oldValue || oldValue === '-' || oldValue.trim() === '' || (Array.isArray(oldArr) && !oldArr.length);
                    const isNewEmpty = !newValue || newValue === '-' || newValue.trim() === '' || (Array.isArray(newArr) && !newArr.length);

                    const stageName = getStageNameFromMaster();
                    const stageLabel = stageName ? (stageName.toLowerCase().includes('stage') ? `from "${stageName}"` : `from "${stageName}" stage`) : "from stage";

                    if (isOldEmpty && newValue && newValue !== '-') {
                        const taskLabel = newValue.toLowerCase().includes('task') ? newValue : `"${newValue}" task`;
                        actionChanges.push(`created ${taskLabel}`);
                    } else if (isNewEmpty && oldValue && oldValue !== '-') {
                        const taskLabel = oldValue.toLowerCase().includes('task') ? oldValue : `"${oldValue}" task`;
                        actionChanges.push(`deleted ${taskLabel} ${stageLabel}`);
                    } else {
                        const parseList = (str: string): string[] => str.split(',').map(s => s.trim()).filter(Boolean);
                        const oldTasks = parseList(oldValue);
                        const newTasks = parseList(newValue);
                        const removed = oldTasks.filter(t => !newTasks.includes(t));
                        const added = newTasks.filter(t => !oldTasks.includes(t));

                        if (removed.length > 0 && added.length === 0) {
                            const formatted = removed.map(t => t.toLowerCase().includes('task') ? t : `"${t}" task`).join(', ');
                            actionChanges.push(`deleted ${formatted} ${stageLabel}`);
                        } else if (added.length > 0 && removed.length === 0) {
                            const formatted = added.map(t => t.toLowerCase().includes('task') ? t : `"${t}" task`).join(', ');
                            actionChanges.push(`created ${formatted}`);
                        } else {
                            const taskName = getTaskNameFromMaster(key);
                            const taskLabel = taskName ? `Tasks (${taskName})` : 'Tasks';
                            filteredChanges.push({ isNew: false, text: `${taskLabel} from ${oldValue} to ${newValue}` });
                        }
                    }
                } else if (key.startsWith('contracts')) {
                    const isSalesRep = key.toLowerCase().includes('salesrepresentative');
                    const label = isSalesRep ? 'Sales Representative in Contracts' : 'Contracts';
                    if (!oldValue || oldValue === '-' || oldValue.trim() === '' || oldValue === 'undefined') {
                        filteredChanges.push({ isNew: true, text: `${label} to ${newValue}` });
                    } else {
                        filteredChanges.push({ isNew: false, text: `${label} from ${oldValue} to ${newValue}` });
                    }
                } else if (key.startsWith('stagesNameList') || key.startsWith('stagesData') || key.toLowerCase() === 'stages' || key.toLowerCase() === 'stage') {
                    const oldArr = oldData?.[key];
                    const newArr = changes?.[key];
                    const isOldEmpty = !oldValue || oldValue === '-' || oldValue.trim() === '' || (Array.isArray(oldArr) && !oldArr.length);
                    const isNewEmpty = !newValue || newValue === '-' || newValue.trim() === '' || (Array.isArray(newArr) && !newArr.length);

                    if (isOldEmpty && newValue && newValue !== '-') {
                        const stageLabel = newValue.toLowerCase().includes('stage') ? newValue : `"${newValue}" stage`;
                        actionChanges.push(`created ${stageLabel}`);
                    } else if (isNewEmpty && oldValue && oldValue !== '-') {
                        const stageLabel = oldValue.toLowerCase().includes('stage') ? oldValue : `"${oldValue}" stage`;
                        actionChanges.push(`deleted ${stageLabel} from project`);
                    } else {
                        const parseList = (str: string): string[] => str.split(',').map(s => s.trim()).filter(Boolean);
                        const oldStages = parseList(oldValue);
                        const newStages = parseList(newValue);
                        const removed = oldStages.filter(s => !newStages.includes(s));
                        const added = newStages.filter(s => !oldStages.includes(s));

                        if (removed.length > 0 && added.length === 0) {
                            const formatted = removed.map(s => s.toLowerCase().includes('stage') ? s : `"${s}" stage`).join(', ');
                            actionChanges.push(`deleted ${formatted} from project`);
                        } else if (added.length > 0 && removed.length === 0) {
                            const formatted = added.map(s => s.toLowerCase().includes('stage') ? s : `"${s}" stage`).join(', ');
                            actionChanges.push(`created ${formatted}`);
                        } else {
                            filteredChanges.push({ isNew: false, text: `Stages from ${oldValue} to ${newValue}` });
                        }
                    }
                } else if (key === 'highlighters' || key.toLowerCase().includes('highlighter')) {
                    const resolveHighlighters = (val: unknown): string[] => {
                        if (!val || val === '-') return [];
                        const resolveName = (item: unknown): string => {
                            if (!item) return '';
                            if (typeof item === 'object' && item !== null) {
                                const itemObj = item as Record<string, unknown>;
                                return String(itemObj.name || itemObj._id || itemObj.id || '');
                            }
                            const idStr = String(item);
                            const found = allHighlighters?.find((hl: IHighlighterItem) => hl?._id === idStr || (hl as { id?: string })?.id === idStr);
                            return found?.name || idStr;
                        };
                        if (Array.isArray(val)) {
                            return val.map(resolveName).filter(Boolean);
                        }
                        const single = resolveName(val);
                        return single ? [single] : [];
                    };

                    const oldNames = resolveHighlighters(rawOldValue !== undefined ? rawOldValue : oldData?.[key]);
                    const newNames = resolveHighlighters(changes[key]);

                    const removed = oldNames.filter(name => !newNames.includes(name));
                    const added = newNames.filter(name => !oldNames.includes(name));

                    const oldDisplay = oldNames.join(', ');

                    if (removed.length > 0 && added.length === 0) {
                        const formatted = removed.map(n => `"${n}"`).join(', ');
                        const fromContext = oldDisplay ? ` from previous Highlighters (${oldDisplay})` : '';
                        actionChanges.push(`removed ${formatted}${fromContext}`);
                    } else if (added.length > 0 && removed.length === 0) {
                        const formatted = added.map(n => `"${n}"`).join(', ');
                        const fromContext = oldDisplay ? ` to Highlighters (previous: ${oldDisplay})` : '';
                        actionChanges.push(`added ${formatted}${fromContext}`);
                    } else if (added.length > 0 && removed.length > 0) {
                        const fromContext = oldDisplay ? ` from Highlighters (${oldDisplay})` : '';
                        actionChanges.push(`added ${added.map(n => `"${n}"`).join(', ')} and removed ${removed.map(n => `"${n}"`).join(', ')}${fromContext}`);
                    } else if (oldValue === '-' || !oldNames.length) {
                        filteredChanges.push({ isNew: true, text: `Highlighters ${newValue}` });
                    } else {
                        filteredChanges.push({ isNew: false, text: `${displayKey} from ${oldValue} to ${newValue}` });
                    }
                } else if (key.toLowerCase().includes('sowmapping')) {
                    const getSowNameFromId = (id: string): string => {
                        if (!id) return '';

                        const extractArray = (target: unknown): Record<string, unknown>[] => {
                            if (!target) return [];
                            if (Array.isArray(target)) return target as Record<string, unknown>[];
                            if (typeof target === 'object' && target !== null) {
                                const obj = target as Record<string, unknown>;
                                if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
                                if (typeof obj.allTaskSow === 'object' && obj.allTaskSow !== null) {
                                    const sowObj = obj.allTaskSow as Record<string, unknown>;
                                    if (Array.isArray(sowObj.data)) return sowObj.data as Record<string, unknown>[];
                                }
                            }
                            return [];
                        };

                        const taskSowList: Record<string, unknown>[] = [];
                        if (Array.isArray(allTaskSow)) {
                            for (const element of allTaskSow) {
                                const arr = extractArray(element);
                                if (arr.length > 0) {
                                    taskSowList.push(...arr);
                                } else if (element && typeof element === 'object' && element !== null) {
                                    const elObj = element as Record<string, unknown>;
                                    if (elObj._id || elObj.id) {
                                        taskSowList.push(elObj);
                                    }
                                }
                            }
                        } else {
                            taskSowList.push(...extractArray(allTaskSow));
                        }

                        if (taskSowList.length > 0) {
                            const found = taskSowList.find((s: Record<string, unknown>) => s?._id === id || s?.id === id);
                            if (found) {
                                const res = (found.sopItem || found.name || found.taskName || found.title) as string | undefined;
                                if (res) return String(res);
                            }
                        }

                        const tasksList = extractArray(allTasks);
                        if (tasksList.length > 0) {
                            for (const t of tasksList) {
                                const sowArr = extractArray(t?.sowMapping || t?.sowmapping || t?.sow || t?.sows);
                                if (sowArr.length > 0) {
                                    const found = sowArr.find((s: Record<string, unknown> | string) => (typeof s === 'object' && s !== null ? ((s as Record<string, unknown>)._id === id || (s as Record<string, unknown>).id === id) : s === id));
                                    if (found && typeof found === 'object' && found !== null) {
                                        const foundObj = found as Record<string, unknown>;
                                        const res = (foundObj.sopItem || foundObj.name || foundObj.taskName || foundObj.title) as string | undefined;
                                        if (res) return String(res);
                                    }
                                }
                            }
                        }

                        return id;
                    };

                    const resolveSowMapping = (val: unknown): string[] => {
                        if (!val || val === '-') return [];
                        const resolveName = (item: unknown): string => {
                            if (!item) return '';
                            if (typeof item === 'object') {
                                const obj = item as Record<string, unknown>;
                                const nameVal = String(obj?.sopItem || obj?.name || obj?.title || obj?.taskName || '');
                                if (nameVal && !/^[0-9a-fA-F]{24}$/.test(nameVal)) return nameVal;
                                const idVal = String(obj?._id || obj?.id || '');
                                if (idVal) return getSowNameFromId(idVal);
                            }
                            const strItem = String(item);
                            if (/^[0-9a-fA-F]{24}$/.test(strItem)) {
                                return getSowNameFromId(strItem);
                            }
                            return strItem;
                        };
                        if (Array.isArray(val)) {
                            return val.map(resolveName).filter(Boolean);
                        }
                        const single = resolveName(val);
                        return single ? [single] : [];
                    };

                    const oldNames = resolveSowMapping(rawOldValue !== undefined ? rawOldValue : oldData?.[key]);
                    const newNames = resolveSowMapping(changes[key]);

                    const removed = oldNames.filter(name => !newNames.includes(name));
                    const added = newNames.filter(name => !oldNames.includes(name));
                    const oldDisplay = oldNames.join(', ');
                    const prevText = oldDisplay ? `\n• previous: ${oldDisplay}` : '';

                    if (removed.length > 0 && added.length === 0) {
                        const removedText = removed.map(n => `"${n}"`).join(', ');
                        actionChanges.push(`removed ${removedText} from SowMapping${prevText}`);
                    } else if (added.length > 0 && removed.length === 0) {
                        const addedText = added.map(n => `"${n}"`).join(', ');
                        actionChanges.push(`added ${addedText} to SowMapping${prevText}`);
                    } else if (added.length > 0 && removed.length > 0) {
                        const addedText = added.map(n => `"${n}"`).join(', ');
                        const removedText = removed.map(n => `"${n}"`).join(', ');
                        actionChanges.push(`updated SowMapping:\n• added: ${addedText}\n• removed: ${removedText}${prevText}`);
                    } else if (oldValue === '-' || !oldNames.length) {
                        filteredChanges.push({ isNew: true, text: `SowMapping ${newValue}` });
                    } else {
                        filteredChanges.push({ isNew: false, text: `${displayKey} from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'timeSpend' || key === 'timeSpent' || key === 'timeTaken') {
                    // Ignore TimeSpend entries since logged time entry handles time tracking display
                } else if (key === 'meetings' && oldValue === '-') {
                    const fromVal = (oldValue === '-' || !oldValue) ? '0' : oldValue;
                    filteredChanges.push({ isNew: false, text: `Meetings from ${fromVal} to ${newValue}` });
                } else if (key === 'siteVisitPin' && oldValue === '-') {
                    filteredChanges.push({ isNew: true, text: `SiteVisitPin to ${newValue}` });
                } else if (key === 'toDisplay') {
                    const stageName = getStageNameFromMaster();
                    const stageLabel = stageName ? (stageName.toLowerCase().includes('stage') ? stageName : `${stageName} stage`) : 'stage';
                    if (changes[key] === true || changes[key] === 'true') {
                        actionChanges.push(`activated ${stageLabel}`);
                    } else {
                        actionChanges.push(`deactivated ${stageLabel}`);
                    }
                } else if (key === 'isDeleted') {
                    if (changes[key] === true || changes[key] === 'true') {
                        const levelStr = String(changes?.level || activity || '').toLowerCase();
                        const isStage = levelStr.includes('stage');
                        const isTask = levelStr.includes('task');
                        if (isTask) {
                            const taskName = getTaskNameFromMaster() || String(changes?.name || oldData?.name || '');
                            const stageName = getStageNameFromMaster();
                            const formattedTask = taskName ? (taskName.toLowerCase().includes('task') ? taskName : `"${taskName}" task`) : "task";
                            const formattedStage = stageName ? (stageName.toLowerCase().includes('stage') ? `from "${stageName}"` : `from "${stageName}" stage`) : "from stage";
                            actionChanges.push(`deleted ${formattedTask} ${formattedStage}`);
                        } else if (isStage) {
                            const stageName = getStageNameFromMaster() || String(changes?.temporaryName || oldData?.temporaryName || changes?.name || oldData?.name || '');
                            const formattedStage = stageName ? (stageName.toLowerCase().includes('stage') ? stageName : `"${stageName}" stage`) : "stage";
                            actionChanges.push(`deleted ${formattedStage} from project`);
                        } else {
                            const name = getTaskNameFromMaster() || getStageNameFromMaster() || String(changes?.temporaryName || oldData?.temporaryName || changes?.name || oldData?.name || '');
                            actionChanges.push(name ? `deleted "${name}" from project` : `deleted item from project`);
                        }
                    } else {
                        filteredChanges.push({ isNew: false, text: `${displayKey} from ${oldValue} to ${newValue}` });
                    }
                } else if (key === 'isOnHold') {
                    if (changes[key] === true || changes[key] === 'true') {
                        actionChanges.push('put on hold');
                    } else {
                        actionChanges.push('resumed');
                    }
                } else if ((key === 'status' || key === 'stageStatus' || key === 'taskStatus') && String(changes[key] || '').toLowerCase().replace(/[\s_-]/g, '') === 'inprogress') {
                    actionChanges.push('started timer');
                } else if (key.startsWith('taskFields.') || key === 'taskFields' || key.toLowerCase().includes('taskfields')) {
                    const keysInChanges = Object.keys(changes || {});
                    const hasTimeEstimate = keysInChanges.some(k => k.toLowerCase().replace(/[\s_-]/g, '').includes('timeestimate'));
                    const keyLower = (key + ' ' + String(newValue) + ' ' + String(oldValue)).toLowerCase();
                    const isTimeEstField = keyLower.includes('time estimate') || keyLower.includes('timeestimate') || keyLower.includes('time_estimate');
                    if (hasTimeEstimate && isTimeEstField) {
                        // Skip redundant taskFields entry when timeEstimate is already present
                    } else if (oldValue === '-') {
                        filteredChanges.push({ isNew: true, text: `${key} to ${newValue}` });
                    } else {
                        filteredChanges.push({ isNew: false, text: `${key} from ${oldValue} to ${newValue}` });
                    }
                } else if (key.toLowerCase().includes('projectsow.fields')) {
                    let cleanVal = String(newValue || '').replace(/\?/g, '');
                    if (cleanVal.toLowerCase().includes('true')) {
                        cleanVal = cleanVal.replace(/:\s*true/gi, ' to yes').replace(/\btrue\b/gi, 'yes');
                    } else if (cleanVal.toLowerCase().includes('false')) {
                        cleanVal = cleanVal.replace(/:\s*false/gi, ' to no').replace(/\bfalse\b/gi, 'no');
                    }
                    if (!cleanVal.includes(' to ')) {
                        cleanVal = `${cleanVal} to yes`;
                    }
                    filteredChanges.push({ isNew: true, text: `ProjectSow. ${cleanVal}` });
                } else if (key === 'tmQuote') {
                    if (oldValue === '-') oldValue = 'No';
                    filteredChanges.push({ isNew: false, text: `T&M Quote from ${oldValue} to ${newValue}` });
                } else if (key === 'projectSow' && (changes[key] as Record<string, unknown>)?.description) {
                    const description = String((changes[key] as Record<string, unknown>).description);
                    const displayDescription = isModal ? description : truncateDescription(description);
                    filteredChanges.push({ isNew: true, text: `${displayKey}: ${displayDescription}` });
                } else if (key === 'description' || key === 'projectSow.description') {
                    const description = String(newValue);
                    const displayDescription = isModal ? description : truncateDescription(description);
                    if (oldValue === '-' || !oldValue || oldValue.trim() === '' || oldValue === 'undefined' || oldValue.toLowerCase() === 'undefined') {
                        filteredChanges.push({ isNew: true, text: `Description to ${displayDescription}` });
                    } else {
                        const oldDisplay = isModal ? oldValue : truncateDescription(oldValue);
                        filteredChanges.push({ isNew: false, text: `Description from ${oldDisplay} to ${displayDescription}` });
                    }
                } else if (key === 'estimatedTimeHistory') {
                    // Ignore estimatedTimeHistory
                } else if (key === 'attachments') {
                    filteredChanges.push({ isNew: false, text: `${displayKey}` });
                } else {
                    if (oldValue === '-' || !oldValue || oldValue.trim() === '' || oldValue === 'undefined' || oldValue.toLowerCase() === 'undefined') {
                        filteredChanges.push({ isNew: true, text: `${displayKey} to ${newValue}` });
                    } else {
                        filteredChanges.push({ isNew: false, text: `${displayKey} from ${oldValue} to ${newValue}` });
                    }
                }
            }
        });
    }

    const actionUpdateText = handleActionUpdate(changes, oldData);
    if (!filteredChanges.length && !roleChanges.length && !actionChanges.length && !actionUpdateText) return '';

    const uniqueFilteredChanges: Array<{ isNew: boolean; text: string }> = [];
    filteredChanges.forEach((item) => {
        const isTaskChange = item.text.startsWith('Tasks') && (item.text.includes('from') || item.text.includes('to'));
        const isStageChange = item.text.startsWith('Stages') && (item.text.includes('from') || item.text.includes('to'));
        const isTimeEstimateChange = (item.text.toLowerCase().includes('timeestimate') || item.text.toLowerCase().includes('time estimate')) && (item.text.includes('from') || item.text.includes('to'));

        if (isTimeEstimateChange) {
            const existingIndex = uniqueFilteredChanges.findIndex(c => (c.text.toLowerCase().includes('timeestimate') || c.text.toLowerCase().includes('time estimate')) && (c.text.includes('from') || c.text.includes('to')));
            if (existingIndex !== -1) {
                if (uniqueFilteredChanges[existingIndex].text.toLowerCase().includes('taskfields') && !item.text.toLowerCase().includes('taskfields')) {
                    uniqueFilteredChanges[existingIndex] = item;
                }
                return;
            }
        }
        if (isTaskChange) {
            const existingIndex = uniqueFilteredChanges.findIndex(c => c.text.startsWith('Tasks') && (c.text.includes('from') || c.text.includes('to')));
            if (existingIndex !== -1) {
                if (item.text.length > uniqueFilteredChanges[existingIndex].text.length) {
                    uniqueFilteredChanges[existingIndex] = item;
                }
                return;
            }
        }
        if (isStageChange) {
            const existingIndex = uniqueFilteredChanges.findIndex(c => c.text.startsWith('Stages from') || c.text.startsWith('Stages to'));
            if (existingIndex !== -1) {
                if (item.text.length > uniqueFilteredChanges[existingIndex].text.length) {
                    uniqueFilteredChanges[existingIndex] = item;
                }
                return;
            }
        }
        uniqueFilteredChanges.push(item);
    });

    const formattedFilteredChanges = uniqueFilteredChanges.map(c => `${c.isNew ? 'created' : 'updated'} ${c.text}`);
    const combinedList: string[] = [...roleChanges, ...actionChanges, ...formattedFilteredChanges];

    const formatCombinedList = (list: string[], scopeLabel: string): string => {
        if (!list || list.length === 0) return `updated ${scopeLabel}`.trim();
        if (list.length === 1) {
            const item = list[0];
            const lower = item.toLowerCase();
            if (lower.includes('from project') || lower.includes('in project') || lower.includes('from stage') || lower.includes('in stage')) {
                return item.trim();
            }
            return `${item} ${scopeLabel}`.trim();
        }
        const cleanScope = scopeLabel.replace(/^in\s+/, '').trim();
        return `updated ${cleanScope}:\n• ${list.join('\n• ')}`;
    };

    switch (activity) {
        case "PROJECT_CREATE":
            return `created project`;
        case "PROJECT_UPDATE":
            return formatCombinedList(combinedList, "in project");
        case "STAGE_CREATE":
        case "STAGES_CREATE": {
            return "";
        }
        case "STAGE_UPDATE":
        case "STAGES_UPDATE": {
            if (combinedList.some(c => c.startsWith('activated') || c.startsWith('deactivated') || c.startsWith('deleted stage') || c.startsWith('deleted task'))) {
                return combinedList.length > 1 ? `\n• ${combinedList.join('\n• ')}` : combinedList.join(', ');
            }
            if (actionChanges.includes('started timer')) {
                const taskName = getTaskNameFromMaster();
                const taskLabel = taskName ? `in "${taskName}" task` : "in task";
                const otherChanges = combinedList.filter(c => c !== 'started timer');
                if (otherChanges.length > 0) {
                    const stageName = getStageNameFromMaster();
                    const stageLabel = stageName ? `in "${stageName}" stage` : "in stage";
                    return `started timer ${taskLabel}\n• ${otherChanges.join('\n• ')} ${stageLabel}`;
                }
                return `started timer ${taskLabel}`;
            }
            const stageName = getStageNameFromMaster();
            const stageLabel = stageName ? `in "${stageName}" stage` : "in stage";
            return formatCombinedList(combinedList, stageLabel);
        }
        case "TASK_CREATE":
        case "TASKS_CREATE": {
            const taskName = getTaskNameFromMaster();
            const stageName = getStageNameFromMaster();
            const formattedTask = taskName ? (taskName.toLowerCase().includes('task') ? taskName : `"${taskName}" task`) : "task";
            const formattedStage = stageName ? (stageName.toLowerCase().includes('stage') ? `in "${stageName}"` : `in "${stageName}" stage`) : "";
            return `created ${formattedTask} ${formattedStage}`.trim();
        }
        case "TASK_UPDATE":
        case "TASKS_UPDATE": {
            const actionText = handleActionUpdate(changes, oldData);
            if (actionText && (actionText.startsWith('deleted subtask') || actionText.includes('subtask is deleted'))) {
                const taskName = getTaskNameFromMaster();
                const taskLabel = taskName ? `in "${taskName}" task` : "in task";
                return `${actionText} ${taskLabel}`;
            }
            if (combinedList.some(c => c.startsWith('activated') || c.startsWith('deactivated') || c.startsWith('deleted stage') || c.startsWith('deleted task'))) {
                return combinedList.length > 1 ? `\n• ${combinedList.join('\n• ')}` : combinedList.join(', ');
            }
            const taskName = getTaskNameFromMaster();
            const taskLabel = taskName ? `in "${taskName}" task` : "in task";
            return formatCombinedList(combinedList, taskLabel);
        }
        case "ACTION_UPDATE": {
            const taskName = getTaskNameFromMaster();
            const taskLabel = taskName ? `in "${taskName}" task` : "in task";
            const actionText = handleActionUpdate(changes, oldData);
            return actionText ? `${actionText} ${taskLabel}` : "";
        }
        case "ACTION_CREATE": {
            const taskName = getTaskNameFromMaster();
            const taskLabel = taskName ? `in "${taskName}" task` : "in task";
            const payload = changes?.payload as { Description?: unknown; description?: unknown } | undefined;
            const descriptionList = payload?.Description || payload?.description || changes?.Description || changes?.description;

            let names: string[] = [];
            if (Array.isArray(descriptionList)) {
                names = (descriptionList as unknown[]).map((item: unknown) => typeof item === 'string' ? item : (typeof item === 'object' && item !== null ? String((item as Record<string, unknown>).name || (item as Record<string, unknown>).description || (item as Record<string, unknown>).comment || (item as Record<string, unknown>).title || '') : '')).filter(Boolean);
            } else if (typeof descriptionList === 'string' && descriptionList.trim()) {
                names = [descriptionList.trim()];
            } else if (changes?.name || changes?.subtaskName) {
                names = [String(changes.name || changes.subtaskName)];
            }

            if (names.length === 1 && !names[0].includes(',') && !names[0].includes('\n')) {
                const displayFirst = truncateDescription(names[0], 10);
                if (displayFirst) {
                    return `created subtask: "${displayFirst}" ${taskLabel}`;
                }
            }
            return `created subtasks ${taskLabel}`;
        }
        case "ACTION_DELETE": {
            const taskName = getTaskNameFromMaster();
            const taskLabel = taskName ? `in "${taskName}" task` : "in task";
            return `subtask is deleted ${taskLabel}`;
        }
        case "TIMETRACKING_UPDATE":
        case "TIMETRACKING_CREATE":
        case "TIME_TRACKING_UPDATE":
        case "TIME_TRACKING_CREATE": {
            const taskName = getTaskNameFromMaster();
            const taskLabel = taskName ? `in "${taskName}" task` : "in task";
            const duration = changes?.timeTaken ?? changes?.timeSpend ?? changes?.timeSpent ?? changes?.time;
            const description = (changes?.description || changes?.Description) as string | undefined;
            const formattedDuration = duration !== undefined && duration !== null ? formatTime(Number(duration)) : "";
            const descText = description ? ` (${description})` : "";
            if (formattedDuration) {
                return `logged time: ${formattedDuration}${descText} ${taskLabel}`;
            }
            return description ? `updated time tracking (${description}) ${taskLabel}` : `updated time tracking ${taskLabel}`;
        }
        case "MEETING_CA_PHASE_CREATE": {
            const desc = (changes?.description || changes?.Description || changes?.name || changes?.Name || changes?.meetingNotes || changes?.meetingnotes) as string | undefined;
            const descVal = desc ? mapValue('description', desc) : '';
            if (descVal && descVal !== '-') {
                return `created Description to ${descVal} in meeting`;
            }
            return "created meeting in meeting";
        }
        case "MEETING_CA_PHASE_UPDATE": {
            return formatCombinedList(combinedList, "in meeting");
        }
        case "CHAT_CREATE":
        case "CHAT_UPDATE": {
            const actionVerb = activity === "CHAT_UPDATE" ? "updated" : "created";
            const msg = (changes?.message || changes?.Message || oldData?.message || oldData?.Message) as string | undefined;
            const type = (changes?.type || changes?.Type || oldData?.type || oldData?.Type || "GENERAL") as string;
            if (msg) {
                return `${actionVerb} thread message as ${msg} in ${type} Type.`;
            }
            return `${actionVerb} thread message in ${type} Type.`;
        }
        default:
            return formatCombinedList(combinedList, "details");
    }
};
